import {
  CodeInput,
  CodeInputFromAPI,
  CodeItem,
  CodeOutput,
  CodeOutputFromAPI,
  ConflictResolution,
  InputType,
} from 'pages/AutomationEngine/dialogs/ActionWizard/subsections/CodeStep/types';
import { pluginMapper } from '@kizenapps/engine/util';
import { AutomationStep } from './types';
import { AutomationStepConfig } from '@kizenapps/engine';

type InputSource =
  | 'object_field'
  | 'related_object_field'
  | 'variable'
  | 'business_plugin_config'
  | 'static_value';

type AutomationStepConfigApiInput = CodeInputFromAPI & {
  label: string;
  script_alias?: string;
  input_source: InputSource;
  hint_field_name: string | null;
  hint_related_object_field_name: string | null;
};

type AutomationStepConfigApiOutput = CodeOutputFromAPI & {
  label: string;
  script_alias?: string;
  conflict_resolution: string;
  create_field_options: boolean;
  input_source: InputSource;
  hint_field_name: string | null;
  hint_related_object_field_name: string | null;
};

export type AutomationStepAction = SnakeToCamelCase<
  Omit<AutomationStepConfigApi, 'secrets'> & {
    secrets: Array<CodeItem>;
    inputs: Array<CodeInput>;
    outputs: Array<CodeOutput>;
  }
>;

export interface AutomationStepConfigApi {
  action_step_api_name: string;
  overall_description?: string;
  action_description: string;
  action_type?: string;
  script_runtime: string;
  step_history_template: null;
  secrets: Array<string>;
  script: string;
  inputs: Array<AutomationStepConfigApiInput>;
  outputs: Array<AutomationStepConfigApiOutput>;
  name: string;
  thumbnail: string;
  plugin_app_api_name: string;
}

const inputSourceLookup: Record<InputSource, InputType> = {
  object_field: 'field',
  related_object_field: 'related_field',
  variable: 'variable',
  business_plugin_config: 'business_plugin_config',
  static_value: 'static_value',
};

const conflictResolutionLookup: Record<string, ConflictResolution> = {
  overwrite: 'overwrite',
  update_if_blank: 'update_if_blank',
  add_only: 'add_only',
};

export const automationMapper = (
  automation_action_configs: AutomationStepConfigApi[],
  apiName: string,
  plugin: any
): AutomationStep[] => {
  if (!automation_action_configs?.length) {
    return [];
  }

  const overallDescription =
    plugin.agentic_description ||
    automation_action_configs[0].overall_description;

  const actions: AutomationStepConfig['actions'] = automation_action_configs
    .sort((a, b) => a.name.localeCompare(b.name))
    .reduce((prev, config) => {
      const {
        action_step_api_name,
        overall_description,
        action_description,
        action_type,
        script_runtime,
        step_history_template,
        ...configRest
      } = config;

      const secrets = config?.secrets?.map((secret) => ({
        name: `${apiName}__${secret}`,
      }));

      const inputs = config?.inputs?.map(
        ({
          name,
          label,
          data_type,
          script_alias,
          input_source,
          hint_field_name,
          hint_related_object_field_name,
          is_list,
          ...rest
        }) => ({
          name,
          label,
          dataType: data_type,
          inputType: input_source && inputSourceLookup[input_source],
          inputSource: input_source && inputSourceLookup[input_source],
          hintFieldName: hint_field_name,
          hintRelatedObjectFieldName: hint_related_object_field_name,
          isList: is_list,
          ...rest,
        })
      );
      const outputs = config?.outputs?.map(
        ({
          name,
          label,
          data_type,
          script_alias,
          input_source,
          hint_field_name,
          hint_related_object_field_name,
          conflict_resolution,
          create_field_options,
          ...rest
        }) => ({
          name,
          label,
          dataType: data_type,
          inputSource: input_source && inputSourceLookup[input_source],
          outputType: input_source && inputSourceLookup[input_source],
          hintFieldName: hint_field_name,
          hintRelatedObjectFieldName: hint_related_object_field_name,
          conflictResolution:
            conflictResolutionLookup[conflict_resolution] ||
            conflict_resolution,
          createMissingOption: create_field_options,
          ...rest,
        })
      );

      return {
        ...prev,
        [config.action_step_api_name]: {
          ...configRest,
          actionStepApiName: action_step_api_name,
          overallDescription: overall_description,
          actionDescription: action_description,
          scriptRuntime: script_runtime,
          stepHistoryTemplate: step_history_template,
          secrets,
          inputs,
          outputs,
        },
      };
    }, {});

  return [
    pluginMapper(
      {
        name: plugin.name,
        thumbnail: plugin.thumbnail,
        overallDescription,
        actions,
      },
      apiName,
      plugin
    ),
  ];
};
